import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import VerticalFeedSDK, { VerticalFeedSDKProps, VideoItem } from './components/VerticalFeedSDK';

export type { VerticalFeedSDKProps, VideoItem };

export interface SDKOptions extends Omit<VerticalFeedSDKProps, 'items'> {
  items: VideoItem[];
}

export interface SDKInstance {
  destroy: () => void;
  update: (options: Partial<SDKOptions>) => void;
}

class VerticalFeedSDKManager {
  private instances: Map<string, { root: Root; container: HTMLElement; options: SDKOptions }> = new Map();

  /**
   * Initialize the vertical feed SDK in a container
   * @param containerId - CSS selector or HTMLElement where the SDK should mount
   * @param options - SDK configuration options
   * @returns SDK instance with destroy and update methods
   */
  init(containerId: string | HTMLElement, options: SDKOptions): SDKInstance {
    let container: HTMLElement;

    if (typeof containerId === 'string') {
      const element = document.querySelector(containerId);
      if (!element) {
        throw new Error(`Container element not found: ${containerId}`);
      }
      container = element as HTMLElement;
    } else {
      container = containerId;
    }

    // Clean up existing instance if any
    if (this.instances.has(container.id || containerId.toString())) {
      this.destroy(container.id || containerId.toString());
    }

    // Hide the original UI by hiding body children (except our container)
    const bodyChildren = Array.from(document.body.children);
    bodyChildren.forEach((child) => {
      if (child !== container && child.tagName !== 'SCRIPT') {
        (child as HTMLElement).style.display = 'none';
      }
    });

    // Add classes to body/html to prevent scrolling
    document.body.classList.add('vertical-feed-sdk-active');
    document.documentElement.classList.add('vertical-feed-sdk-active');

    // Create a unique ID for this instance
    const instanceId = container.id || `vertical-feed-${Date.now()}`;
    if (!container.id) {
      container.id = instanceId;
    }

    // Create React root and render
    const root = createRoot(container);
    root.render(React.createElement(VerticalFeedSDK, options));

    // Store instance
    this.instances.set(instanceId, {
      root,
      container,
      options: { ...options },
    });

    // Return instance API
    return {
      destroy: () => this.destroy(instanceId),
      update: (newOptions: Partial<SDKOptions>) => {
        const instance = this.instances.get(instanceId);
        if (instance) {
          const updatedOptions = { ...instance.options, ...newOptions };
          instance.root.render(React.createElement(VerticalFeedSDK, updatedOptions));
          instance.options = updatedOptions;
        }
      },
    };
  }

  /**
   * Destroy an SDK instance
   * @param instanceId - Instance ID or container selector
   */
  destroy(instanceId: string): void {
    const instance = this.instances.get(instanceId);
    if (instance) {
      instance.root.unmount();
      this.instances.delete(instanceId);

      // Restore the original UI
      const bodyChildren = Array.from(document.body.children);
      bodyChildren.forEach((child) => {
        if (child.tagName !== 'SCRIPT') {
          (child as HTMLElement).style.display = '';
        }
      });

      // Remove SDK active classes
      document.body.classList.remove('vertical-feed-sdk-active');
      document.documentElement.classList.remove('vertical-feed-sdk-active');
    }
  }

  /**
   * Destroy all SDK instances
   */
  destroyAll(): void {
    this.instances.forEach((_, id) => this.destroy(id));
    
    // Ensure UI is restored even if instances map is empty
    const bodyChildren = Array.from(document.body.children);
    bodyChildren.forEach((child) => {
      if (child.tagName !== 'SCRIPT') {
        (child as HTMLElement).style.display = '';
      }
    });
    document.body.classList.remove('vertical-feed-sdk-active');
    document.documentElement.classList.remove('vertical-feed-sdk-active');
  }
}

// Create singleton instance
const sdkManager = new VerticalFeedSDKManager();

// Export for UMD global
const VerticalFeedSDKGlobal = {
  init: (containerId: string | HTMLElement, options: SDKOptions) => {
    return sdkManager.init(containerId, options);
  },
  destroy: (instanceId: string) => {
    sdkManager.destroy(instanceId);
  },
  destroyAll: () => {
    sdkManager.destroyAll();
  },
};

// Export types for TypeScript (these won't be in the bundle)
export type { VerticalFeedSDKProps, VideoItem };

// Note: We don't export the VerticalFeedSDK component directly for UMD builds
// Users access it through the default export API (init method)

// Default export becomes the UMD global (window.VerticalFeedSDK)
// This is what client applications will use when loading via script tag
export default VerticalFeedSDKGlobal;
